import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { formatDate } from '../utils/dateFormat';
import '../styles/AdminDashboard.css';

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const sellerFilter = searchParams.get('seller');

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await api.get('/admin/products');
      setProducts(response.data);
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (productId, newStatus) => {
    try {
      await api.put(`/admin/products/${productId}/status`, { status: newStatus });
      fetchProducts();
    } catch (err) {
      console.error('Erreur:', err);
    }
  };

  const handleDelete = async (productId) => {
    if (window.confirm(t('admin.products.confirmDelete'))) {
      try {
        await api.delete(`/admin/products/${productId}`);
        fetchProducts();
      } catch (err) {
        console.error('Erreur:', err);
      }
    }
  };

  const filteredProducts = products.filter(p => {
    if (sellerFilter && p.user_id !== parseInt(sellerFilter)) return false;
    if (filter === 'all') return true;
    return p.status === filter;
  });

  const sellerName = sellerFilter && filteredProducts.length > 0
    ? filteredProducts[0].username : null;

  if (loading) {
    return <div className="loading-container"><p>{t('common.loading')}</p></div>;
  }

  return (
    <div className="admin-container">
      <h2>{t('admin.products.title')}</h2>

      {sellerFilter && (
        <div style={{ marginBottom: 15, padding: '10px 15px', background: '#e8dbf5', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{t('admin.products.sellerFilter', { name: sellerName || `#${sellerFilter}`, count: filteredProducts.length })}</span>
          <button onClick={() => navigate('/admin/products')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18 }}>&times;</button>
        </div>
      )}

      <div className="admin-filters">
        <select value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="all">{t('admin.products.allProducts', { count: products.length })}</option>
          <option value="active">{t('admin.products.onlineProducts', { count: products.filter(p => p.status === 'active').length })}</option>
          <option value="pending">{t('admin.products.pendingProducts', { count: products.filter(p => p.status === 'pending').length })}</option>
          <option value="sold">{t('admin.products.soldProducts', { count: products.filter(p => p.status === 'sold').length })}</option>
        </select>
      </div>

      <table className="admin-table">
        <thead>
          <tr>
            <th>{t('common.id')}</th>
            <th>{t('common.title')}</th>
            <th>{t('common.seller')}</th>
            <th>{t('common.price')}</th>
            <th>{t('common.status')}</th>
            <th>{t('common.date')}</th>
            <th>{t('common.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {filteredProducts.map(product => (
            <tr key={product.id}>
              <td>{product.id}</td>
              <td>{product.title}</td>
              <td>{product.username}</td>
              <td>{parseFloat(product.price).toFixed(2)} CHF</td>
              <td>
                <span className={`status-badge status-${product.status}`}>
                  {product.status}
                </span>
              </td>
              <td>{formatDate(product.created_at)}</td>
              <td>
                <select
                  value={product.status}
                  onChange={(e) => handleStatusChange(product.id, e.target.value)}
                >
                  <option value="pending">{t('status.pending')}</option>
                  <option value="active">{t('status.active')}</option>
                  <option value="sold">{t('status.sold')}</option>
                </select>
                <button
                  className="btn-delete-small"
                  onClick={() => handleDelete(product.id)}
                >
                  {t('common.delete')}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AdminProducts;
